/* eslint-disable no-magic-numbers */
import type { ActionableCourse } from '../services/courses/interfaces'
import type { HistoricalCourse } from '../services/goals/interfaces'
import type { MemberTrainingCourse, WaiveData } from '../services/memberTraining/interfaces'

export const ADVANCED_PLANS: {
	TIER_ONE: number
	TIER_TWO: number
	TIER_THREE: number
	TRAINING_MATERIALS_MINIMUM: number
} = {
	TIER_ONE: 125,
	TIER_THREE: 375,
	TIER_TWO: 325,
	TRAINING_MATERIALS_MINIMUM: 500,
}

export const CHARACTERS: {
	MAX: number
	MIN: number
	PASSWORD: number
	PHONE: { max: number, min: number }
	ZIP: { max: number, min: number }
} = Object.freeze({
	MAX: 500,
	MIN: 3,
	PASSWORD: 8,
	PHONE: { max: 14, min: 10 },
	ZIP: { max: 5, min: 5 },
})
export const COMPLETE = 100

export const COMPLETE_PLANS: {
	TIER_ONE: number
	TIER_TWO: number
	TIER_THREE: number
	TRAINING_MATERIALS_MINIMUM: number
} = {
	TIER_ONE: 150,
	TIER_THREE: 400,
	TIER_TWO: 350,
	TRAINING_MATERIALS_MINIMUM: 550,
}

export const COMPLIANCE_DAYS: Record<string, number | undefined> = {
	CO: 45,
	IL: 30,
}

export const COUNTDOWN = 4
export const CONFIG_HEADER = { headers: { Accept: 'application/json' } }

export const DEFAULT_SELECTED_CURRICULUM_NAME = '[Curriculum name]'
export const DEFAULT_TITLE = 'CareAcademy'
export const DEFAULT_TITLE_DESIGN_SYSTEM = '- Design System - CareAcademy'
export const DEFAULT_TITLE_TEAM = '- Team - CareAcademy'
export const DELAY: {
	FIFTY_MILLISECONDS: number
	FIVE_SECONDS: number
	HALF_SECOND: number
	NINE_SECONDS: number
	ONE_HUNDRED_MILLISECONDS: number
	ONE_SECOND: number
	TEN_SECONDS: number
	THREE_SECONDS: number
	TWO_SECONDS: number
} = Object.freeze({
	FIFTY_MILLISECONDS: 50,
	FIVE_SECONDS: 5000,
	HALF_SECOND: 500,
	NINE_SECONDS: 9000,
	ONE_HUNDRED_MILLISECONDS: 100,
	ONE_SECOND: 1000,
	TEN_SECONDS: 10000,
	THREE_SECONDS: 3000,
	TWO_SECONDS: 2000,
})

export const DATEPICKER_PATTERN = '/^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1])\/\d{4}$/'
export const DESIRED_SEATS: {
	DECREMENT_VALUE: number
	INCREMENT_VALUE: number
	INITIAL_VALUE: number
	TIER_ONE: number
	TIER_TWO: number
	TIER_THREE: number
	UPDATED_LESS_THAN_30: number
	UPDATED_GREATER_THAN_30: number
} = {
	DECREMENT_VALUE: 25,
	INCREMENT_VALUE: 35,
	INITIAL_VALUE: 30,
	TIER_ONE: 25,
	TIER_THREE: 35,
	TIER_TWO: 30,
	UPDATED_GREATER_THAN_30: 31,
	UPDATED_LESS_THAN_30: 27,
}

export const DURATION: {
	FILTER: number
	LONG: number
	LONGER: number
	MEDIUM: number
	SHORT: number
	STEP: number
} = {
	FILTER: 99999, // randomly high number to represent "no filter"
	LONG: 45,
	LONGER: 60,
	MEDIUM: 30,
	SHORT: 15,
	STEP: 10,
}

export const EMPTY_FIELD = { error: '', value: '' }

export const ESSENTIALS_PLANS: {
	TIER_ONE: number
	TIER_TWO: number
	TIER_THREE: number
	TRAINING_MATERIALS_MINIMUM: number
} = {
	TIER_ONE: 100,
	TIER_THREE: 350,
	TIER_TWO: 300,
	TRAINING_MATERIALS_MINIMUM: 250,
}

export const FREQUENCY: {
	CLASSES_COUNT: number
	COUNT: number
} = Object.freeze({
	CLASSES_COUNT: 1,
	COUNT: 1,
})
export const INVALID_KEYS = [
	'-',
	'e',
	'+',
	'.',
	'ArrowUp',
	'ArrowDown',
]

export const MOVE_TO = {
	DURATION: 200,
	DURATION_LONG: 400,
	TOLERANCE: 100,
	TOP: 200,
}

export const NO_RESULTS_TEXT = { CATALOG: 'Nothing matches your filters or search terms. Try adjusting your selections or using a different keyword.' }

export const PAGINATION: {
	DEFAULT: number
	ITEMS: number
} = Object.freeze({
	DEFAULT: 1,
	ITEMS: 10,
})

export const PER_PAGE: {
	COMMAND: number
	COURSES: number
	CURRENT: number
	DEFAULT: number
	ITEMS: number
	MEMBERS: number
	VIDEOS: number
} = Object.freeze({
	COMMAND: 7,
	COURSES: 24,
	CURRENT: 1,
	DEFAULT: 100,
	ITEMS: 20,
	MEMBERS: 25,
	VIDEOS: 20,
})
export const PERCENTAGE_ERROR_TEXT = 'Must be a number between 0-100, or left blank'

export const PROGRESS_TIME: {
	END: number
	START: number
} = Object.freeze({
	END: 100,
	START: 0,
})

export const REGEX = {
	EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
	PHONE: /^\d{10}$|^\(\d{3}\) \d{3}-\d{4}$|^\d{3}-?\d{3}-?\d{4}$/,
}

export const SEAT_COUNT: {
	INCLUDED: number
	MAX: number
	MIN: number
	STEP: number
} = Object.freeze({
	INCLUDED: 25,
	MAX: 1000,
	MIN: 0,
	STEP: 5,
})
export const STATUS_CODE: {
	BAD_REQUEST: number
	FORBIDDEN: number
	INTERNAL_SERVER_ERROR: number
	MULTIPLE_CHOICES: number
	OK: number
	NO_CONTENT: number
	NOT_FOUND: number
	UNPROCESSABLE_ENTITY: number
} = {
	BAD_REQUEST: 400,
	FORBIDDEN: 403,
	INTERNAL_SERVER_ERROR: 500,
	MULTIPLE_CHOICES: 300,
	NOT_FOUND: 404,
	NO_CONTENT: 204,
	OK: 200,
	UNPROCESSABLE_ENTITY: 422,
}
export const SHARED_COMPLETIONS_LEARN_MORE_LINK = 'https://help.careacademy.com/hc/en-us/articles/36546177472653-Shared-Training-History-Reuse-Training-Completed-with-CareAcademy'
export const STROKE_INNER = '#287080'
export const STROKE_INNER_OVER = '#8F2C2C'
export const STROKE_OUTER = '#F2FBFC'
export const STROKE_OUTER_OVER = '#FFEBEB'
export const TIME: {
	DAYS_IN_MONTH: number
	DAYS_IN_YEAR: number
	DAYS: number
	FLOOR: number
	HOURS: number
	MILLISECONDS: number
	MINUTES: number
	MONTHS: number
	REMAINING_DAYS: number
	TOMORROW: number
	WEEKS: number
	YEAR: number
} = Object.freeze({
	DAYS: 7,
	DAYS_IN_MONTH: 31,
	DAYS_IN_YEAR: 365,
	FLOOR: 100,
	HOURS: 24,
	MILLISECONDS: 300,
	MINUTES: 60,
	MONTHS: 12,
	REMAINING_DAYS: 42,
	TOMORROW: 24 * 60 * 60 * 1000,
	WEEKS: 26,
	YEAR: 1970,
})
export const VALID_NUMERIC_KEYS: string[] = [
	'0',
	'1',
	'2',
	'3',
	'4',
	'5',
	'6',
	'7',
	'8',
	'9',
	'Backspace',
	'Delete',
	'Enter',
	'Escape',
	'Tab',
	'ArrowUp',
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
]
export const Z_INDEX = 9999

export const hiddenElemSelector = '.hide, .js-reveal--element-hidden, .dn'

export function getDefaultCourse(): MemberTrainingCourse {
	return {
		add_to_library: false,
		courseStatusActions: {
			renaming: false,
			selected: false,
			type: 'courses',
		},
		course_status_id: null,
		description: '',
		id: null,
		is_external: false,
		length_in_minutes: 0,
		limited_usage: false,
		name: '',
		topics: [],
		training: {
			completionDate: '',
			location: '',
			organization: '',
			trainer: '',
		},
		type: 'offline_agency',
	}
}

export function getDefaultHistoricalCourse(): HistoricalCourse {
	return {
		added_in_bundle: false,
		caregiver_id: null,
		course_id: null,
		course_status_id: null,
		description: '',
		external_courses: [],
		goal: {
			due_date: '',
			is_new: false,
			saveClassList: false,
		},
		id: null,
		name: '',
		training: { organization: '' },
		type: undefined,
	}
}

export function getDefaultWaiveClass(): ActionableCourse {
	return {
		add_to_library: false,
		agency_id: 0,
		caregiver_ids: '',
		course: {
			description: '',
			name: '',
			training: {
				completedDate: '',
				date: '',
				location: '',
				trainer: '',
				waived_completion_course_status_id: null,
			},
			type: 'offline_agency',
		},
		course_status_id: null,
	}
}

export function getDefaultWaiveData(): WaiveData {
	return {
		action: 'waive_class',
		caregiverId: null,
		courseData: {
			courseDesc: '',
			courseDuration: TIME.MINUTES,
			isLmsCourse: false,
		},

		courseName: '',
		courseStatusId: null,
		goalHasComplianceMandates: false,
		is_external: false,
		test: '',
		training: {
			completionDate: '',
			location: '',
			organization: '',
			trainer: '',
		},

		type: 'online_careacademy',
	}
}
/* eslint-enable no-magic-numbers */
